import { useMemo, useState } from 'react';
import cn from 'classnames';
import A_CircleButton from '@/components/A_Atoms/A_CircleButton/A_CircleButton';
import M_ChatConversationItem from '@/components/M_Molecules/M_ChatConversationItem/M_ChatConversationItem';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import { Conversation } from '@/services/chatApi';
import { getTranslation } from '@/constants/translations';
import { useLanguage } from '@/contexts/LanguageContext';
import { conversationTitle } from '@/utils/chat';
import styles from './O_ChatSidebar.module.css';

interface O_ChatSidebarProps {
  conversations: Conversation[];
  activeConversationId?: string;
  currentUserId?: string;
  isLoading: boolean;
  /** Ключ перевода ошибки загрузки списка. */
  error?: string | null;
  onSelect: (conversation: Conversation) => void;
  onCreate: () => void;
}

export default function O_ChatSidebar({
  conversations,
  activeConversationId,
  currentUserId,
  isLoading,
  error,
  onSelect,
  onCreate,
}: O_ChatSidebarProps) {
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language, key);
  const [query, setQuery] = useState('');

  const visibleConversations = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return conversations;

    return conversations.filter(conversation => {
      const title = conversationTitle(conversation, '').toLowerCase();
      const username = conversation.peer?.username?.toLowerCase() || '';
      return (
        title.includes(normalizedQuery) || username.includes(normalizedQuery)
      );
    });
  }, [conversations, query]);

  return (
    <aside className={styles.wrapper}>
      <div className={styles.header}>
        <h4 className={styles.title}>{t('chat.list.title')}</h4>
        <A_CircleButton
          variant='primary'
          size='small'
          icon={
            <Q_Icon.PlusIcon
              width={20}
              height={20}
              fill='none'
              stroke='white'
            />
          }
          onClick={onCreate}
        />
      </div>

      <div className={styles.search}>
        <Q_Icon.SearchIcon width={20} height={20} fill='none' />
        <input
          type='text'
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder={t('chat.list.searchPlaceholder')}
          className={cn(styles.search_input, 'text-p-8')}
        />
      </div>

      <div className={styles.list}>
        {visibleConversations.map(conversation => (
          <M_ChatConversationItem
            key={conversation.id}
            conversation={conversation}
            currentUserId={currentUserId}
            isActive={conversation.id === activeConversationId}
            onClick={onSelect}
          />
        ))}

        {visibleConversations.length === 0 && (
          <p className={cn(styles.empty, 'text-caption')}>
            {error
              ? t(error)
              : isLoading
                ? t('chat.list.loading')
                : query.trim()
                  ? t('chat.list.nothingFound')
                  : t('chat.list.empty')}
          </p>
        )}
      </div>
    </aside>
  );
}
